/*─────────────────────────────────────────────────────────────────────────────
 * ProtectedRoute.tsx
 *
 * Core access-control gate for module-level permissions.
 *
 * Architecture:
 *   - `ProtectedRoute` — generic component that checks a single moduleId/level
 *   - `createProtectedRoute(slug, level?)` — factory that generates typed HOC
 *     wrappers for each module; eliminates the 18 near-identical components
 *     that used to exist in this file.
 *   - `AuthenticatedRoute` — lightweight gate that only checks isAuthenticated
 *     (used for Appearance & User Profile which require no module permission)
 *
 * Public API (unchanged — all existing named exports are preserved):
 *   UsersProtectedRoute, RoleProfilesProtectedRoute, LogProfilesProtectedRoute,
 *   ApiKeysProtectedRoute, TelephonyServersProtectedRoute,
 *   TelephonyDomainsProtectedRoute, TenantsProtectedRoute,
 *   BrandingProtectedRoute, SystemMiscProtectedRoute,
 *   EmailSettingsProtectedRoute, LogSettingsProtectedRoute,
 *   AuditLogsProtectedRoute, UserActivityProtectedRoute,
 *   ChangeHistoryProtectedRoute, SecurityLogsProtectedRoute,
 *   AppearanceProtectedRoute, UserProfileProtectedRoute,
 *   SipProfilesProtectedRoute, FeatureCodesProtectedRoute,
 *   GatewaysProtectedRoute, WeakPasswordsProtectedRoute,
 *   BackupRestoreProtectedRoute, EmailTemplatesProtectedRoute,
 *   IntegrationsProtectedRoute
 *─────────────────────────────────────────────────────────────────────────────*/

import React, { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions, MODULE_SLUGS } from '../hooks/usePermissions';
import { useAuthStore } from '../store/auth';
import { moduleIdsService } from '../services/moduleIds';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProtectedRouteProps {
  children: ReactNode;
  /** Module slug string or numeric module ID */
  moduleId: string | number;
  /** Minimum permission level required to access the route. Defaults to READ. */
  requiredLevel?: 'READ' | 'WRITE' | 'FULL';
  /** Path to redirect to when the user lacks the required permission. */
  fallbackPath?: string;
}

// Convenience type for the HOC wrappers generated by createProtectedRoute
type RouteWrapper = (props: { children: ReactNode }) => React.ReactElement;

// ---------------------------------------------------------------------------
// Core generic gate
// ---------------------------------------------------------------------------

/**
 * ProtectedRoute
 *
 * Renders `children` only if the current user has the required permission for
 * `moduleId`. Redirects to `fallbackPath` otherwise.
 *
 * While module IDs are being resolved from the service cache the component
 * shows a minimal loading indicator to avoid a flash of the protected content.
 */
export default function ProtectedRoute({
  children,
  moduleId,
  requiredLevel = 'READ',
  fallbackPath = '/unauthorized',
}: ProtectedRouteProps) {
  const { hasModulePermission } = usePermissions();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (typeof moduleId === 'number') {
      // Numeric IDs are always immediately resolvable
      setIsLoading(false);
      return;
    }

    const checkReady = async () => {
      if (!moduleIdsService.isServiceLoaded()) {
        // Service not yet loaded — wait for it
        await moduleIdsService.loadModuleIds();
      } else {
        // Service loaded but this particular module may be missing from cache
        const { resolveModuleId } = await import('../services/moduleIds');
        if (resolveModuleId(moduleId) === null) {
          console.warn(`[ProtectedRoute] Module "${moduleId}" not in cache — forcing reload`);
          await moduleIdsService.forceReload();
        }
      }
      setIsLoading(false);
    };

    checkReady();
  }, [moduleId]);

  // Show a minimal spinner while the ID cache is warming up
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (!hasModulePermission(moduleId, requiredLevel)) {
    return (
      <Navigate
        to={fallbackPath}
        state={{ from: location.pathname, requiredModule: moduleId, requiredLevel }}
        replace
      />
    );
  }

  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// Factory helper
// ---------------------------------------------------------------------------

/**
 * createProtectedRoute
 *
 * Generates a named HOC that wraps `ProtectedRoute` for a specific module slug
 * and permission level. This avoids writing the same three-line wrapper for
 * every protected module.
 *
 * @param slug - MODULE_SLUGS key that identifies the module
 * @param level - Minimum required permission level (default: READ)
 *
 * @example
 * export const UsersProtectedRoute = createProtectedRoute(MODULE_SLUGS.USERS);
 */
function createProtectedRoute(
  slug: string,
  level: 'READ' | 'WRITE' | 'FULL' = 'READ',
): RouteWrapper {
  return ({ children }: { children: ReactNode }) => (
    <ProtectedRoute moduleId={slug} requiredLevel={level}>
      {children}
    </ProtectedRoute>
  );
}

// ---------------------------------------------------------------------------
// Lightweight auth-only gate (no module permission needed)
// ---------------------------------------------------------------------------

/**
 * AuthenticatedRoute
 *
 * Renders `children` for any authenticated user, regardless of module
 * permissions. Used for globally-accessible pages like Appearance and Profile.
 */
function AuthenticatedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/**
 * PrimaryAdminRoute
 *
 * Renders `children` ONLY if the user is the primary system administrator.
 * Used for strictly global operations like Licensing.
 */
function PrimaryAdminRoute({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.isPrimaryAdmin) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// Named HOC exports — generated via factory (API-compatible with previous file)
// ---------------------------------------------------------------------------

// Admin section
export const UsersProtectedRoute             = createProtectedRoute(MODULE_SLUGS.USERS);
export const RoleProfilesProtectedRoute      = createProtectedRoute(MODULE_SLUGS.ROLE_PROFILES);
export const LogProfilesProtectedRoute       = createProtectedRoute(MODULE_SLUGS.LOG_PROFILES);
export const ApiKeysProtectedRoute           = createProtectedRoute(MODULE_SLUGS.API_KEYS);
export const TelephonyServersProtectedRoute  = createProtectedRoute(MODULE_SLUGS.TELEPHONY_SERVERS);
export const TelephonyDomainsProtectedRoute  = createProtectedRoute(MODULE_SLUGS.TELEPHONY_DOMAINS);
export const TenantsProtectedRoute           = createProtectedRoute(MODULE_SLUGS.TENANTS);
export const TenantBrandingProtectedRoute    = createProtectedRoute(MODULE_SLUGS.TENANT_BRANDING);
export const BrandingProtectedRoute          = createProtectedRoute(MODULE_SLUGS.BRANDING);
export const SystemMiscProtectedRoute        = createProtectedRoute(MODULE_SLUGS.SYSTEM_MISC);
export const EmailSettingsProtectedRoute     = createProtectedRoute(MODULE_SLUGS.EMAIL_SETTINGS);
export const EmailTemplatesProtectedRoute    = createProtectedRoute(MODULE_SLUGS.EMAIL_TEMPLATES);
export const CertificatesProtectedRoute      = createProtectedRoute(MODULE_SLUGS.CERTIFICATES);
export const HttpServerProtectedRoute        = createProtectedRoute(MODULE_SLUGS.HTTP_SERVER);
export const BackupRestoreProtectedRoute     = createProtectedRoute(MODULE_SLUGS.BACKUP_RESTORE);
export const IntegrationsProtectedRoute      = createProtectedRoute(MODULE_SLUGS.INTEGRATIONS);
export const MaintenanceProtectedRoute       = createProtectedRoute(MODULE_SLUGS.MAINTENANCE);
export const AdminCronProfilesProtectedRoute = createProtectedRoute(MODULE_SLUGS.ADMIN_CRON_PROFILES);
export const AdminCronJobsProtectedRoute     = createProtectedRoute(MODULE_SLUGS.ADMIN_CRON_JOBS);

// Firewall & Network section
export const AccessControlProtectedRoute     = createProtectedRoute(MODULE_SLUGS.ACCESS_CONTROL);
export const FirewallServicesProtectedRoute  = createProtectedRoute(MODULE_SLUGS.FIREWALL_SERVICES);
export const FirewallRulesProtectedRoute     = createProtectedRoute(MODULE_SLUGS.FIREWALL_RULES);
export const GeoFirewallProtectedRoute       = createProtectedRoute(MODULE_SLUGS.GEO_FIREWALL);
export const OpenVpnServerProtectedRoute     = createProtectedRoute(MODULE_SLUGS.OPENVPN_SERVER);
export const OpenVpnClientProtectedRoute     = createProtectedRoute(MODULE_SLUGS.OPENVPN_CLIENT);

// PBX Extensions section
export const ExtensionsProtectedRoute        = createProtectedRoute(MODULE_SLUGS.EXTENSIONS);
export const HotDeskingProtectedRoute        = createProtectedRoute(MODULE_SLUGS.HOT_DESKING);
export const BulkModificationProtectedRoute  = createProtectedRoute(MODULE_SLUGS.BULK_MODIFICATION);
export const ExtensionStatusProtectedRoute   = createProtectedRoute(MODULE_SLUGS.EXTENSION_STATUS);

// PBX Call Center section
export const RingGroupsProtectedRoute        = createProtectedRoute(MODULE_SLUGS.RING_GROUPS);
export const QueuesProtectedRoute            = createProtectedRoute(MODULE_SLUGS.QUEUES);
export const CallbackProfilesProtectedRoute  = createProtectedRoute(MODULE_SLUGS.CALLBACK_PROFILES);
export const VipListsProtectedRoute          = createProtectedRoute(MODULE_SLUGS.VIP_LISTS);
export const QueuePrioritiesProtectedRoute   = createProtectedRoute(MODULE_SLUGS.QUEUE_PRIORITIES);
export const AgentsProtectedRoute            = createProtectedRoute(MODULE_SLUGS.AGENTS);

// PBX Routing section
export const OutboundRoutesProtectedRoute    = createProtectedRoute(MODULE_SLUGS.OUTBOUND_ROUTES);
export const InboundRoutesProtectedRoute     = createProtectedRoute(MODULE_SLUGS.INBOUND_ROUTES);
export const DynamicRoutingProtectedRoute    = createProtectedRoute(MODULE_SLUGS.DYNAMIC_ROUTING);
export const GatewaysProtectedRoute          = createProtectedRoute(MODULE_SLUGS.GATEWAYS);
export const TrunksProtectedRoute            = createProtectedRoute(MODULE_SLUGS.TRUNKS);

// PBX Applications section
export const ConferencesProtectedRoute       = createProtectedRoute(MODULE_SLUGS.CONFERENCES);
export const CustomApplicationsProtectedRoute = createProtectedRoute(MODULE_SLUGS.CUSTOM_APPLICATIONS);
export const CustomDestinationProtectedRoute = createProtectedRoute(MODULE_SLUGS.CUSTOM_DESTINATION);
export const CustomContextProtectedRoute     = createProtectedRoute(MODULE_SLUGS.CUSTOM_CONTEXT);
export const FeatureCodesProtectedRoute      = createProtectedRoute(MODULE_SLUGS.FEATURE_CODES);
export const PagingGroupsProtectedRoute      = createProtectedRoute(MODULE_SLUGS.PAGING_GROUPS);
export const PickupGroupsProtectedRoute      = createProtectedRoute(MODULE_SLUGS.PICKUP_GROUPS);
export const ParkingLotsProtectedRoute       = createProtectedRoute(MODULE_SLUGS.PARKING_LOTS);
export const SpeedDialsProtectedRoute        = createProtectedRoute(MODULE_SLUGS.SPEED_DIALS);
export const VoicemailBroadcastProtectedRoute = createProtectedRoute(MODULE_SLUGS.VOICEMAIL_BROADCAST);
export const CallbackRulesProtectedRoute     = createProtectedRoute(MODULE_SLUGS.CALLBACK_RULES);
export const DisaEntriesProtectedRoute       = createProtectedRoute(MODULE_SLUGS.DISA_ENTRIES);
export const PinListsProtectedRoute          = createProtectedRoute(MODULE_SLUGS.PIN_LISTS);
export const DynamicDestinationsProtectedRoute = createProtectedRoute(MODULE_SLUGS.DYNAMIC_DESTINATIONS);
export const DirectDialProtectedRoute        = createProtectedRoute(MODULE_SLUGS.DIRECT_DIAL);
export const DirectRouteProtectedRoute       = createProtectedRoute(MODULE_SLUGS.DIRECT_ROUTE);
export const DialProfilesProtectedRoute      = createProtectedRoute(MODULE_SLUGS.DIAL_PROFILES);
export const CallFlowsProtectedRoute         = createProtectedRoute(MODULE_SLUGS.CALL_FLOWS);

// PBX Class of Service section
export const CosClassProtectedRoute          = createProtectedRoute(MODULE_SLUGS.COS_CLASS);
export const CosFeatureCategoriesProtectedRoute = createProtectedRoute(MODULE_SLUGS.COS_FEATURE_CATEGORIES);
export const CosDialRestrictionProtectedRoute = createProtectedRoute(MODULE_SLUGS.COS_DIAL_RESTRICTION);
export const CosCustomerCodesProtectedRoute  = createProtectedRoute(MODULE_SLUGS.COS_CUSTOMER_CODES);
export const CosAuthCodesProtectedRoute      = createProtectedRoute(MODULE_SLUGS.COS_AUTH_CODES);
export const CosRouteSelectionsProtectedRoute = createProtectedRoute(MODULE_SLUGS.COS_ROUTE_SELECTIONS);

// PBX Provisioning section
export const ProvisioningFirmwareProtectedRoute = createProtectedRoute(MODULE_SLUGS.PROVISIONING_FIRMWARE);
export const ProvisioningTemplatesProtectedRoute = createProtectedRoute(MODULE_SLUGS.PROVISIONING_TEMPLATES);
export const ProvisioningDevicesProtectedRoute = createProtectedRoute(MODULE_SLUGS.PROVISIONING_DEVICES);

// PBX Tools section
export const BlacklistProtectedRoute         = createProtectedRoute(MODULE_SLUGS.BLACKLIST);
export const DashboardProtectedRoute         = createProtectedRoute(MODULE_SLUGS.DASHBOARD);
export const LogViewerProtectedRoute         = createProtectedRoute(MODULE_SLUGS.LOG_VIEWER);
export const CronProfilesProtectedRoute      = createProtectedRoute(MODULE_SLUGS.CRON_PROFILES);
export const WeakPasswordsProtectedRoute     = createProtectedRoute(MODULE_SLUGS.WEAK_PASSWORDS);
export const PhonebooksProtectedRoute        = createProtectedRoute(MODULE_SLUGS.PHONEBOOKS);
export const TaskManagerProtectedRoute       = createProtectedRoute(MODULE_SLUGS.TASK_MANAGER);
export const TimeGroupsProtectedRoute        = createProtectedRoute(MODULE_SLUGS.TIME_GROUPS);
export const TimeConditionsProtectedRoute    = createProtectedRoute(MODULE_SLUGS.TIME_CONDITIONS);
export const AnnouncementsProtectedRoute     = createProtectedRoute(MODULE_SLUGS.ANNOUNCEMENTS);
export const LanguagesProtectedRoute         = createProtectedRoute(MODULE_SLUGS.LANGUAGES);
export const CidModifiersProtectedRoute      = createProtectedRoute(MODULE_SLUGS.CID_MODIFIERS);
export const CidLookupProtectedRoute         = createProtectedRoute(MODULE_SLUGS.CID_LOOKUP);
export const ToolsAuthCodesProtectedRoute    = createProtectedRoute(MODULE_SLUGS.TOOLS_AUTH_CODES);
export const VoiceGuidesProtectedRoute       = createProtectedRoute(MODULE_SLUGS.VOICE_GUIDES);
export const IvrsProtectedRoute              = createProtectedRoute(MODULE_SLUGS.IVRS);
export const PbxCliProtectedRoute            = createProtectedRoute(MODULE_SLUGS.PBX_CLI);

// PBX Fax / Hotel / Emergency section
export const VirtualFaxProtectedRoute        = createProtectedRoute(MODULE_SLUGS.VIRTUAL_FAX);
export const HotelManagementProtectedRoute   = createProtectedRoute(MODULE_SLUGS.HOTEL_MANAGEMENT);
export const HotelSettingsProtectedRoute     = createProtectedRoute(MODULE_SLUGS.HOTEL_SETTINGS);
export const EmergencyNumbersProtectedRoute  = createProtectedRoute(MODULE_SLUGS.EMERGENCY_NUMBERS);
export const EmergencyLocationsProtectedRoute = createProtectedRoute(MODULE_SLUGS.EMERGENCY_LOCATIONS);

// Reports section
export const CallDetailRecordsProtectedRoute = createProtectedRoute(MODULE_SLUGS.CALL_DETAIL_RECORDS);
export const CdrFiltersProtectedRoute        = createProtectedRoute(MODULE_SLUGS.CDR_FILTERS);
export const PbxSipEndpointsProtectedRoute   = createProtectedRoute(MODULE_SLUGS.PBX_SIP_ENDPOINTS);
export const AuditLogsProtectedRoute         = createProtectedRoute(MODULE_SLUGS.AUDIT_LOGS);
export const UserActivityProtectedRoute      = createProtectedRoute(MODULE_SLUGS.USER_ACTIVITY);
export const ChangeHistoryProtectedRoute     = createProtectedRoute(MODULE_SLUGS.CHANGE_HISTORY);
export const SecurityLogsProtectedRoute      = createProtectedRoute(MODULE_SLUGS.SECURITY_LOGS);

// Settings section
export const SystemGeneralProtectedRoute     = createProtectedRoute(MODULE_SLUGS.SYSTEM_GENERAL);
export const SystemVarsProtectedRoute        = createProtectedRoute(MODULE_SLUGS.SYSTEM_VARS);
export const ConferenceProfilesProtectedRoute = createProtectedRoute(MODULE_SLUGS.CONFERENCE_PROFILES);
export const QueueProfilesProtectedRoute     = createProtectedRoute(MODULE_SLUGS.QUEUE_PROFILES);
export const EslUsersProtectedRoute          = createProtectedRoute(MODULE_SLUGS.ESL_USERS);
export const MicrosoftTeamsProtectedRoute    = createProtectedRoute(MODULE_SLUGS.MICROSOFT_TEAMS);
export const LogSettingsProtectedRoute       = createProtectedRoute(MODULE_SLUGS.LOG_SETTINGS);
export const MiniHttpProtectedRoute          = createProtectedRoute(MODULE_SLUGS.MINI_HTTP);
export const CdrSettingsProtectedRoute       = createProtectedRoute(MODULE_SLUGS.CDR_SETTINGS);
export const VoicemailSettingsProtectedRoute = createProtectedRoute(MODULE_SLUGS.VOICEMAIL_SETTINGS);

// Voice Prompts / Tech Settings section
export const SipProfilesProtectedRoute       = createProtectedRoute(MODULE_SLUGS.SIP_SETTINGS);
export const VoiceMohProtectedRoute          = createProtectedRoute(MODULE_SLUGS.VOICE_MOH);
export const VoiceRecordingProtectedRoute    = createProtectedRoute(MODULE_SLUGS.VOICE_RECORDING);
export const TechDialProfilesProtectedRoute  = createProtectedRoute(MODULE_SLUGS.TECH_DIAL_PROFILES);
export const SipSettingsProtectedRoute       = createProtectedRoute(MODULE_SLUGS.SIP_SETTINGS);
export const CrmIntegrationsProtectedRoute   = createProtectedRoute(MODULE_SLUGS.CRM_INTEGRATIONS);

// AI System section
export const AiProvidersProtectedRoute       = createProtectedRoute(MODULE_SLUGS.AI_PROVIDERS);
export const AiProfilesProtectedRoute        = createProtectedRoute(MODULE_SLUGS.AI_PROFILES);
export const AiAgentsProtectedRoute          = createProtectedRoute(MODULE_SLUGS.AI_AGENTS);
export const AiToolProfilesProtectedRoute    = createProtectedRoute(MODULE_SLUGS.AI_TOOL_PROFILES);
export const AiTranslatorProfilesProtectedRoute = createProtectedRoute(MODULE_SLUGS.AI_TRANSLATOR_PROFILES);

// Auth-only (no module permission) — accessible to all logged-in users
export const AppearanceProtectedRoute        = AuthenticatedRoute;
export const UserProfileProtectedRoute       = AuthenticatedRoute;

// Global Administrator strictly gated route
export const LicensingProtectedRoute         = PrimaryAdminRoute;
